import React, { useContext, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import PropTypes from "prop-types";
import { CaseActionsContext } from "contexts/CaseActionsContext";
import { EditCaseActionDialog } from "components/CaseManagement/CaseForms/EditCaseActionDialog";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useFetchDeleteV2 } from "hooks/fetch/useFetchDeleteV2";
import { useFetchPutV2 } from "hooks/fetch/useFetchPutV2";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { useQueryClient } from "@tanstack/react-query";

export function ActionCardMenu({ action, caseId }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createActionDialogVisible, setCreateActionDialogVisible] = useState(
    false
  );

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { refetchActions } = useContext(CaseActionsContext);
  const queryClient = useQueryClient();

  const closeMoreMenu = () => {
    setAnchorEl(null);
  };

  const handleError = message => {
    addSnackbarToQueue({
      message: message,
      severity: "error"
    });
  };

  const handleDeleteCompleted = async () => {
    addSnackbarToQueue({
      message: "Case action deleted.",
      severity: "success"
    });
    await queryClient.invalidateQueries(["streamFeed", caseId]);
    await refetchActions();
  };

  const handlePutCompleted = async () => {
    addSnackbarToQueue({
      message: "Case action completed.",
      severity: "success"
    });
    await refetchActions();
  };

  const { deleteCallback: deleteCaseAction } = useFetchDeleteV2(
    `case_actions/${action?.id}`
  );

  const { putCallback: putCaseAction } = useFetchPutV2(
    `case_actions/${action?.id}`
  );

  const deleteItem = () => {
    deleteCaseAction().then(
      _fulfil => handleDeleteCompleted(),
      _reject => handleError("Case action failed to delete.")
    );
  };

  const completeItem = () => {
    let tempAction = { ...action };
    tempAction.action_status = 2;
    putCaseAction(tempAction).then(
      _fulfil => handlePutCompleted(),
      _reject => handleError("Case action failed to complete.")
    );
    closeMoreMenu();
  };

  const editItem = () => {
    setCreateActionDialogVisible(true);
    closeMoreMenu();
  };

  function openMoreMenu(event) {
    setAnchorEl(event.currentTarget);
  }

  const isActionComplete =
    action?.action_status === 2 || action?.action_status === 3;

  return (
    <>
      <IconButton
        onClick={openMoreMenu}
        data-qa="CP-Button-More-Options"
        size="large"
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={closeMoreMenu}
      >
        <MenuItem onClick={editItem} data-qa="CP-Button-Edit-Action">
          Edit
        </MenuItem>
        {!isActionComplete && (
          <Tooltip
            title={action?.is_external ? "Managed by external system" : ""}
          >
            <span>
              <MenuItem
                onClick={completeItem}
                data-qa="CP-Button-Complete-Action"
                disabled={!!action?.is_external}
              >
                Complete
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={action?.is_external ? "Managed by external system" : ""}
        >
          <span>
            <MenuItem
              onClick={() => setDeleteDialogOpen(true)}
              data-qa="CP-Button-Delete-Action"
              disabled={!!action?.is_external}
            >
              Delete
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      {deleteDialogOpen && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          title="Are you sure you want to delete this action?"
          onConfirm={() => {
            deleteItem();
            setDeleteDialogOpen(false);
          }}
          onCancel={() => setDeleteDialogOpen(false)}
          onClose={() => setDeleteDialogOpen(false)}
        />
      )}
      {createActionDialogVisible && (
        <EditCaseActionDialog
          open={createActionDialogVisible}
          setOpen={setCreateActionDialogVisible}
          action={action}
          caseId={caseId}
          onReplaceExistingAction={() => refetchActions()}
        />
      )}
    </>
  );
}

ActionCardMenu.propTypes = {
  action: PropTypes.object,
  menuIcon: PropTypes.element,
  caseId: PropTypes.number
};
